import { apiSlice } from 'src/features/api/api-slice';
import {
  ApiResponse,
  BaseManageUsersData,
  ChangeEmailData,
  DeleteUserData,
  BadgeData,
  RenameUserData,
  ResetStreakData,
  ResetUserPasswordQuery,
  SearchUserQuery,
  ExperimentRoomRebuildEarlyAccessData,
  UpdateFreezeData,
} from 'src/common/interfaces';
import { RTKQueryTag } from 'src/common/constants';
import {
  ManageUserResponse,
  ResetUserPasswordResponse,
} from 'src/features/admin/manage-users/components/user/user.types';

import { ADMIN_MANAGE_USERS_URL, ADMIN_URL } from './manage-users.constants';

export const manageUsersApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    search: builder.query<ApiResponse<ManageUserResponse>, SearchUserQuery>({
      query: ({ data }) => ({
        url: `${ADMIN_MANAGE_USERS_URL}/search/?data=${data}`,
        method: 'GET',
      }),
      providesTags: [RTKQueryTag.ManageUsers],
    }),
    resetPassword: builder.query<ApiResponse<ResetUserPasswordResponse>, ResetUserPasswordQuery>({
      query: ({ userId }) => ({
        url: `${ADMIN_MANAGE_USERS_URL}/reset-password/?userId=${userId}`,
        method: 'GET',
      }),
      providesTags: [RTKQueryTag.ManageUsers],
    }),
    renameUser: builder.mutation<ApiResponse, RenameUserData>({
      query: (body) => ({
        url: `${ADMIN_MANAGE_USERS_URL}/rename`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: [RTKQueryTag.ManageUsers],
    }),
    deleteUser: builder.mutation<ApiResponse, DeleteUserData>({
      query: (body) => ({
        url: `${ADMIN_MANAGE_USERS_URL}/delete`,
        method: 'DELETE',
        body,
      }),
    }),
    banUser: builder.mutation<ApiResponse, BaseManageUsersData>({
      query: (body) => ({
        url: `${ADMIN_MANAGE_USERS_URL}/ban`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: [RTKQueryTag.ManageUsers],
    }),
    applyDiscount: builder.mutation<ApiResponse, BaseManageUsersData>({
      query: (body) => ({
        url: `${ADMIN_MANAGE_USERS_URL}/apply-discount`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: [RTKQueryTag.ManageUsers],
    }),
    resetStreak: builder.mutation<ApiResponse, ResetStreakData>({
      query: (body) => ({
        url: `${ADMIN_MANAGE_USERS_URL}/reset-streak`,
        method: 'PATCH',
        body: { userId: body.userId, days: Number.parseInt(body.days, 10) },
      }),
      invalidatesTags: [RTKQueryTag.ManageUsers],
    }),
    updateFreeze: builder.mutation<ApiResponse, UpdateFreezeData>({
      query: (body) => ({
        url: `${ADMIN_MANAGE_USERS_URL}/update-freeze`,
        method: 'PATCH',
        body: { userId: body.userId, days: Number.parseInt(body.days, 10) },
      }),
      invalidatesTags: [RTKQueryTag.ManageUsers],
    }),
    giveBadge: builder.mutation<ApiResponse, BadgeData>({
      query: (body) => ({
        url: `${ADMIN_MANAGE_USERS_URL}/give-badge`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: [RTKQueryTag.ManageUsers],
    }),
    removeBadge: builder.mutation<ApiResponse, BadgeData>({
      query: (body) => ({
        url: `${ADMIN_MANAGE_USERS_URL}/remove-badge`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: [RTKQueryTag.ManageUsers],
    }),
    stopSubscription: builder.mutation<ApiResponse, BaseManageUsersData>({
      query: (body) => ({
        url: `${ADMIN_MANAGE_USERS_URL}/stop-subscription`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: [RTKQueryTag.ManageUsers],
    }),
    disableTwoFactor: builder.mutation<ApiResponse, BaseManageUsersData>({
      query: (body) => ({
        url: `${ADMIN_URL}/disable-two-factor`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: [RTKQueryTag.ManageUsers],
    }),
    takeoverRooms: builder.mutation<ApiResponse, BaseManageUsersData>({
      query: (body) => ({
        url: `${ADMIN_MANAGE_USERS_URL}/takeover-rooms`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: [RTKQueryTag.ManageUsers],
    }),
    changeEmail: builder.mutation<ApiResponse, ChangeEmailData>({
      query: (body) => ({
        url: `${ADMIN_MANAGE_USERS_URL}/change-email`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: [RTKQueryTag.ManageUsers],
    }),
    experimentsRoomsRebuildEarlyAccess: builder.mutation<ApiResponse, ExperimentRoomRebuildEarlyAccessData>({
      query: (body) => ({
        url: `${ADMIN_MANAGE_USERS_URL}/experiments/room-rebuild-early-access`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: [RTKQueryTag.ManageUsers],
    }),
  }),
});

export const {
  useSearchQuery,
  useLazyResetPasswordQuery,
  useRenameUserMutation,
  useApplyDiscountMutation,
  useBanUserMutation,
  useChangeEmailMutation,
  useDeleteUserMutation,
  useGiveBadgeMutation,
  useResetStreakMutation,
  useUpdateFreezeMutation,
  useRemoveBadgeMutation,
  useTakeoverRoomsMutation,
  useStopSubscriptionMutation,
  useDisableTwoFactorMutation,
  useExperimentsRoomsRebuildEarlyAccessMutation,
} = manageUsersApiSlice;
